import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { DirectoryEntryLink } from '@/components/directory/directory-entry-link'

/**
 * The SHOP submenu, in one place.
 *
 * Both the shared site header and the home hero navigation render this exact
 * component, so the submenu exists once and behaves identically on every page.
 * The markup and class names are the approved ones; the styling lives in the
 * `.mega` block of globals.css.
 */

export type ShopMenuItem = {
    name: string
    desc: string
    kicker: string
    meta: string
    titleLines: [string, string]
    href: string
    tone: 'coral' | 'sky' | 'pink' | 'lilac' | 'lime'
    brand?: string
    icon: 'crypto' | 'username' | 'youtube' | 'account' | 'influencer'
}

export const shopMenuItems: Array<ShopMenuItem> = [
    {
        name: 'Crypto Deals',
        desc: 'Curated market listings',
        kicker: 'Digital assets',
        meta: 'USDT · BTC · ETH',
        titleLines: ['Crypto', 'Deals'],
        href: '/browse?category=crypto-p2p',
        tone: 'coral',
        icon: 'crypto'
    },
    {
        name: 'Usernames',
        desc: 'Premium social handles',
        kicker: 'Social handles',
        meta: '@handles · social',
        titleLines: ['Premium', 'Usernames'],
        href: '/browse?category=usernames',
        tone: 'sky',
        icon: 'username'
    },
    {
        name: 'YouTube Channels',
        desc: 'Established creator properties',
        kicker: 'Creator assets',
        meta: 'Channels · creators',
        titleLines: ['YouTube', 'Channels'],
        href: '/browse?category=youtube-channels',
        tone: 'pink',
        icon: 'youtube'
    },
    {
        name: 'Accounts',
        desc: 'Selected account listings',
        kicker: 'Social assets',
        meta: 'Curated listings',
        titleLines: ['Digital', 'Accounts'],
        href: '/browse?category=social-accounts',
        tone: 'lilac',
        icon: 'account'
    },
    {
        name: 'Influencers',
        desc: 'Discover leading creators',
        kicker: 'Creator directory',
        meta: 'Top creators',
        titleLines: ['Top', 'Influencers'],
        href: '/browse?category=influencers',
        tone: 'lime',
        brand: 'Bazio Directory',
        icon: 'influencer'
    }
]

const CardIcon: FC<{ type: ShopMenuItem['icon'] }> = ({ type }): ReactNode => {
    if (type === 'youtube') {
        return (
            <svg viewBox='0 0 32 32' aria-hidden='true'>
                <rect x='4' y='8' width='24' height='16' rx='6' className='fill' />
                <path d='M14 12.5 21 16l-7 3.5z' fill='var(--ink-900)' />
            </svg>
        )
    }

    if (type === 'influencer') {
        return (
            <svg viewBox='0 0 32 32' aria-hidden='true'>
                <circle cx='12' cy='12' r='4' className='stroke' />
                <circle cx='21' cy='13' r='3.2' className='stroke' />
                <path d='M5.5 25c.7-4.5 3.2-6.8 7.1-6.8s6.4 2.3 7.1 6.8M18.7 19.5c3.7 0 6 1.8 6.7 5.5' className='stroke' />
            </svg>
        )
    }

    if (type === 'account') {
        return (
            <svg viewBox='0 0 32 32' aria-hidden='true'>
                <circle cx='16' cy='11' r='5' className='stroke' />
                <path d='M7 25c.9-5.1 4.1-7.7 9-7.7s8.1 2.6 9 7.7' className='stroke' />
            </svg>
        )
    }

    if (type === 'username') {
        return (
            <svg viewBox='0 0 32 32' aria-hidden='true'>
                <circle cx='16' cy='16' r='10' className='stroke' />
                <path d='M20.8 13.2c0-2.2-1.7-3.7-4.3-3.7-3.4 0-5.8 2.7-5.8 6.4 0 3.6 2.3 6.2 5.6 6.2 2.1 0 3.5-.8 4.2-2.1M20.8 13.2v5.1' className='stroke' />
            </svg>
        )
    }

    return (
        <svg viewBox='0 0 32 32' aria-hidden='true'>
            <circle cx='16' cy='16' r='10' className='stroke' />
            <path d='M16 8v16M20.5 11.5c-.9-.9-2.4-1.5-4.2-1.5-2.5 0-4.3 1.2-4.3 3.1 0 4.8 9 1.7 9 6.2' className='stroke' />
        </svg>
    )
}

/** The dark market card. Shared by the desktop grid and the mobile swipe row. */
export const MarketVisual: FC<{ item: ShopMenuItem }> = ({ item }): ReactNode => (
    <div className={`visual ${item.tone}`}>
        <div className='market-card'>
            <div className='market-top'>
                <span className='market-brand'>
                    {item.brand ?? 'Bazio'}
                </span>

                <span className='status-dot' />
            </div>

            <div className='icon-box'>
                <CardIcon type={item.icon} />
            </div>

            <div className='market-kicker'>
                {item.kicker}
            </div>

            <h3 className={`market-title${item.icon === 'influencer' ? ' influencer-title' : ''}`}>
                {item.titleLines[0]}
                <br />
                {item.titleLines[1]}
            </h3>

            <div className='market-bottom'>
                <div className='divider' />

                <div className='market-meta'>
                    {item.meta}
                </div>
            </div>
        </div>
    </div>
)

interface ShopMegaMenuProps {
    /** Extra class on the panel. `mega-flow` drops it directly under its host. */
    className?: string
    onMouseEnter?: () => void
    onMouseLeave?: () => void
    /** Called when a link inside the panel is followed. */
    onNavigate?: () => void
}

/** Desktop panel. Visibility is driven by a `shop-open` class on an ancestor. */
export const ShopMegaMenu: FC<ShopMegaMenuProps> = ({
    className,
    onMouseEnter,
    onMouseLeave,
    onNavigate
}): ReactNode => (
    <div
        id='shop-mega-menu'
        className={className ? `mega ${className}` : 'mega'}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
    >
        <div className='mega-inner'>
            <aside className='side'>
                <div>
                    <p className='eyebrow'>
                        Explore Bazio
                    </p>

                    {shopMenuItems.map((item) => (
                        <Link key={item.name} className='cat' href={item.href} onClick={onNavigate}>
                            {item.name}
                        </Link>
                    ))}

                    <DirectoryEntryLink source='marketplace_nav' className='cat' onNavigate={onNavigate}>
                        Telegram Marketplaces
                    </DirectoryEntryLink>
                </div>

                <Link className='shop-all' href='/browse' onClick={onNavigate}>
                    <strong>Shop All</strong>
                    <span aria-hidden='true'>→</span>
                </Link>
            </aside>

            <div className='cards'>
                {shopMenuItems.map((item) => (
                    <Link key={item.name} className='product' href={item.href} onClick={onNavigate}>
                        <MarketVisual item={item} />

                        <div className='product-name'>
                            {item.name}
                        </div>

                        <div className='product-desc'>
                            {item.desc}
                        </div>
                    </Link>
                ))}
            </div>
        </div>
    </div>
)

/** The same submenu content, as it appears inside a mobile panel. */
export const ShopMenuMobileContent: FC<{ onNavigate?: () => void }> = ({ onNavigate }): ReactNode => (
    <div className='mobile-shop-content'>
        <div className='mobile-shop-label'>
            Explore Bazio
        </div>

        <div className='mobile-cards'>
            {shopMenuItems.map((item) => (
                <Link
                    key={item.name}
                    className='mobile-product'
                    href={item.href}
                    onClick={onNavigate}
                >
                    <MarketVisual item={item} />

                    <div className='product-name'>
                        {item.name}
                    </div>

                    <div className='product-desc'>
                        {item.desc}
                    </div>
                </Link>
            ))}
        </div>

        <div className='flex flex-col items-start'>
            <Link className='mobile-shop-all' href='/browse' onClick={onNavigate}>
                Shop All
                <span aria-hidden='true'>→</span>
            </Link>

            <DirectoryEntryLink source='marketplace_nav' className='mobile-shop-all' onNavigate={onNavigate}>
                Telegram Marketplaces
                <span aria-hidden='true'>→</span>
            </DirectoryEntryLink>
        </div>
    </div>
)
